'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { HeroBanner } from '@/components/images/hero-banner';
import { HeroPicker } from '@/components/images/hero-picker';
import { Icon } from '@/components/ui/icon';
import { Edit02Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
interface EditableHeroProps {
  /**
   * Owning user's id. Drives both the live placeholder (when `heroUrl`
   * is null) and the post-upload palette via `pickBrandBluePalette`. Pass
   * `null` only when the user record isn't created yet (onboarding
   * pre-signup); `<HeroBanner>` falls back to `displayName` in that case
   * but the picker should be deferred — the live preview palette is wrong
   * without a user id.
   */
  userId: string | null;
  displayName: string | null;
  heroUrl: string | null;
  /**
   * NSFW flag forwarded from the SDK `coverNsfw` field. When `true`
   * the underlying `<HeroBanner>` renders the blurred-overlay state.
   */
  nsfw?: boolean;
  /** Called with the chosen hero URL (or `''` when cleared) once the picker confirms. */
  onHeroChange: (url: string) => void;
  /** LCP hint — pass `true` for the in-viewport profile header. */
  priority?: boolean;
  /** Applied to the outer trigger button — use for layout/positioning. */
  className?: string;
}

/**
 * `<EditableHero>` — single source of truth for the "click to edit cover image"
 * trigger. Wraps `<HeroBanner>` with a hover/focus-visible edit-icon overlay and
 * mounts the shared `<HeroPicker>` modal.
 */
export function EditableHero(t0) {
  const $ = _c(30);
  const {
    userId,
    displayName,
    heroUrl,
    nsfw: t1,
    onHeroChange,
    priority: t2,
    className
  } = t0;
  const nsfw = t1 === undefined ? false : t1;
  const priority = t2 === undefined ? false : t2;
  const t = useTranslations("profile");
  const [open, setOpen] = useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setOpen(true);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== className) {
    t4 = cn("group relative block w-full overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-offset-2", className);
    $[1] = className;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t) {
    t5 = t("hero.edit_overlay");
    $[3] = t;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== displayName || $[6] !== heroUrl || $[7] !== nsfw || $[8] !== priority || $[9] !== userId) {
    t6 = <HeroBanner heroUrl={heroUrl} userId={userId} displayName={displayName} priority={priority} nsfw={nsfw} />;
    $[5] = displayName;
    $[6] = heroUrl;
    $[7] = nsfw;
    $[8] = priority;
    $[9] = userId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={Edit02Icon} className="size-5" aria-hidden="true" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("hero.edit_overlay");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-surface-scrim-default text-white opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none">{t7}<span className="ml-2 text-body font-medium">{t8}</span></span>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t9) {
    t10 = <button type="button" onClick={t3} className={t4} aria-label={t5}>{t6}{t9}</button>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== displayName || $[22] !== heroUrl || $[23] !== onHeroChange || $[24] !== open || $[25] !== userId) {
    t11 = userId !== null && <HeroPicker open={open} onOpenChange={setOpen} userId={userId} currentHeroUrl={heroUrl} searchSeedQuery={displayName ?? ""} onHeroChange={onHeroChange} />;
    $[21] = displayName;
    $[22] = heroUrl;
    $[23] = onHeroChange;
    $[24] = open;
    $[25] = userId;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}